/**
 * Schritteditor: bearbeitbare Karten mit Zeit, Temperatur und Hilfen.
 */
import { placeholderKeys, renderStepText, stepPlainText } from '@cookbook/domain';
import { useRef, useState } from 'react';
import type { RecipeDraft } from '../api/types.ts';
import { useDragItems } from '../lib/useDragReorder.ts';
import { texts } from '../texts/de.ts';
import { AnimationPicker } from './AnimationPicker.tsx';
import { DragHandle } from './DragHandle.tsx';
import { StepMediaEditor } from './StepMediaEditor.tsx';

/** Zutaten des Entwurfs, für Platzhalter und Vorschau. */
type DraftIngredients = RecipeDraft['ingredients'];

/** Eigenschaften des Schritteditors. */
export interface StepListEditorProps {
  readonly steps: RecipeDraft['steps'];
  readonly ingredients: DraftIngredients;
  readonly onChange: (steps: RecipeDraft['steps']) => void;
}

/**
 * Aufklappbarer Bereich mit der Animationsauswahl. Der Inhalt entsteht erst
 * beim Aufklappen, damit lange Rezepte nicht alle Vorschaubilder zeichnen.
 */
function AnimationSection({
  step,
  ingredients,
  onChange,
}: {
  readonly step: RecipeDraft['steps'][number];
  readonly ingredients: DraftIngredients;
  readonly onChange: (
    templateId: string | null,
    params: RecipeDraft['steps'][number]['animationParams'],
  ) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="mt-2 rounded-lg bg-surface-sunken p-2"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer text-xs font-medium">
        {texts.animation.section}
        {step.animationTemplateId === null ? '' : ' ✓'}
      </summary>
      {open ? (
        <>
          <p className="mt-1 mb-2 text-xs text-content-muted">{texts.animation.sectionHint}</p>
          <AnimationPicker
            text={stepPlainText(`${step.title} ${step.instruction}`, ingredients)}
            templateId={step.animationTemplateId}
            params={step.animationParams}
            onChange={onChange}
          />
        </>
      ) : null}
    </details>
  );
}

/**
 * Anleitung mit Platzhaltern: Knöpfe setzen {mehl} an der Schreibmarke ein,
 * darunter steht der Text so, wie er im Rezept erscheint.
 */
function InstructionField({
  value,
  ingredients,
  onChange,
}: {
  readonly value: string;
  readonly ingredients: DraftIngredients;
  readonly onChange: (value: string) => void;
}) {
  const field = useRef<HTMLTextAreaElement | null>(null);
  const named = ingredients.filter((ingredient) => ingredient.name.trim() !== '');
  const keys = placeholderKeys(named);
  const parts = value.includes('{') ? renderStepText(value, named) : [];

  /** Setzt einen Platzhalter an der Schreibmarke ein. */
  function insert(key: string) {
    const element = field.current;
    const token = `{${key}}`;
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);
    // Schreibmarke hinter den Platzhalter setzen, sobald der Text neu gezeichnet ist.
    window.requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(start + token.length, start + token.length);
    });
  }

  return (
    <div>
      <textarea
        ref={field}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={texts.editor.instruction}
        aria-label={texts.editor.instruction}
        rows={3}
        className="w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm"
      />
      {named.length === 0 ? null : (
        <details className="mt-1 text-xs">
          <summary className="cursor-pointer text-content-muted">{texts.scale.placeholders}</summary>
          <p className="mt-1 text-content-muted">{texts.scale.placeholdersHint}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {named.map((ingredient, index) => (
              <button
                key={`${keys[index] ?? index}`}
                type="button"
                onClick={() => insert(keys[index] ?? '')}
                title={ingredient.name}
                className="rounded-full border border-border-subtle bg-surface px-2 py-0.5 font-mono"
              >
                {`{${keys[index] ?? ''}}`}
              </button>
            ))}
          </div>
        </details>
      )}
      {parts.length === 0 ? null : (
        <p className="mt-1 rounded-lg bg-surface-sunken px-2 py-1 text-xs">
          <span className="text-content-muted">{texts.scale.preview}: </span>
          {parts.map((part, index) =>
            part.kind === 'amount' ? (
              <strong key={index} className="text-accent-strong">
                {part.text}
              </strong>
            ) : part.kind === 'unknown' ? (
              <mark
                key={index}
                title={texts.scale.unknownPlaceholder}
                className="rounded bg-accent-soft px-0.5 text-accent-strong"
              >
                {part.text}
              </mark>
            ) : (
              <span key={index}>{part.text}</span>
            ),
          )}
        </p>
      )}
    </div>
  );
}

/** Bearbeitbare Schrittliste. */
export function StepListEditor({ steps, ingredients, onChange }: StepListEditorProps) {
  const drag = useDragItems(steps, (step, index) => step.id ?? `neu-${index}`, onChange);

  // Hochladen dauert; die Änderung soll auf den neuesten Stand der Schritte treffen.
  const latest = useRef(steps);
  latest.current = steps;

  /** Ändert einen Schritt über seinen Schlüssel; bleibt richtig, auch wenn inzwischen verschoben wurde. */
  function updateByKey(key: string, changes: Partial<RecipeDraft['steps'][number]>) {
    const next = latest.current.map((entry, position) =>
      (entry.id ?? `neu-${position}`) === key ? { ...entry, ...changes } : entry,
    );
    latest.current = next;
    onChange(next);
  }

  /** Ändert ein Feld eines Schritts. */
  function update(index: number, changes: Partial<RecipeDraft['steps'][number]>) {
    const next = latest.current.map((entry, position) =>
      position === index ? { ...entry, ...changes } : entry,
    );
    latest.current = next;
    onChange(next);
  }

  /** Verschiebt einen Schritt. */
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    const a = steps[index];
    const b = steps[target];
    if (a === undefined || b === undefined) {
      return;
    }
    const next = [...steps];
    next[index] = b;
    next[target] = a;
    onChange(next);
  }

  return (
    <section className="rounded-card border border-border-subtle bg-surface-raised p-4">
      <h2 className="mb-2 font-semibold">{texts.editor.steps}</h2>

      <ol className="space-y-3">
        {drag.entries.map(({ key, item: step, index }, position) => (
          <li
            key={key}
            {...drag.itemProps(key)}
            className={`rounded-xl border border-border-subtle bg-surface-raised p-3 ${
              drag.dragging === key ? 'relative z-10 shadow-lg ring-2 ring-accent' : ''
            }`}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <DragHandle label={`${texts.editor.steps} ${position + 1}`} {...drag.handleProps(key)} />
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent-strong">
                  {position + 1}
                </span>
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  aria-label={texts.editor.moveUp}
                  className="rounded-lg border border-border-subtle px-2 py-1 text-content-muted"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  aria-label={texts.editor.moveDown}
                  className="rounded-lg border border-border-subtle px-2 py-1 text-content-muted"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => onChange(steps.filter((_, position) => position !== index))}
                  aria-label={texts.editor.remove}
                  className="rounded-lg border border-border-subtle px-2 py-1 text-content-muted"
                >
                  ✕
                </button>
              </div>
            </div>

            <input
              value={step.title}
              onChange={(event) => update(index, { title: event.target.value })}
              placeholder={texts.editor.stepTitle}
              aria-label={texts.editor.stepTitle}
              className="mb-2 w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm font-medium"
            />
            <InstructionField
              value={step.instruction}
              ingredients={ingredients}
              onChange={(instruction) => update(index, { instruction })}
            />

            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2">
                {texts.editor.duration}
                <input
                  type="number"
                  min={0}
                  value={step.durationMinutes ?? ''}
                  onChange={(event) =>
                    update(index, {
                      durationMinutes: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                  className="w-20 rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
                />
              </label>
              <label className="flex items-center gap-2">
                {texts.editor.temperature}
                <input
                  type="number"
                  min={0}
                  value={step.temperatureCelsius ?? ''}
                  onChange={(event) =>
                    update(index, {
                      temperatureCelsius: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                  className="w-20 rounded-lg border border-border-subtle bg-surface px-2 py-1.5"
                />
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={step.passive}
                  onChange={() => update(index, { passive: !step.passive })}
                />
                {texts.editor.passive}
              </label>
              {position === 0 ? null : (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={step.parallel}
                    onChange={() => update(index, { parallel: !step.parallel })}
                  />
                  {texts.editor.parallel}
                </label>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={step.timer}
                  onChange={() => update(index, { timer: !step.timer })}
                />
                {texts.editor.timer}
              </label>
            </div>

            <AnimationSection
              step={step}
              ingredients={ingredients}
              onChange={(animationTemplateId, animationParams) =>
                update(index, { animationTemplateId, animationParams })
              }
            />

            <StepMediaEditor media={step.media} onChange={(media) => updateByKey(key, { media })} />

            <details className="mt-2 rounded-lg bg-surface-sunken p-2">
              <summary className="cursor-pointer text-xs font-medium">
                {texts.editor.hintForBeginners}
              </summary>
              <input
                value={step.whyItWorks}
                onChange={(event) => update(index, { whyItWorks: event.target.value })}
                placeholder={texts.editor.whyItWorks}
                aria-label={texts.editor.whyItWorks}
                className="mt-2 w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-xs"
              />
              <input
                value={step.doneWhen}
                onChange={(event) => update(index, { doneWhen: event.target.value })}
                placeholder={texts.editor.doneWhen}
                aria-label={texts.editor.doneWhen}
                className="mt-2 w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5 text-xs"
              />
            </details>
          </li>
        ))}
      </ol>
    </section>
  );
}
